import React, { useEffect, useRef, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { GradeBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Upload, Download, Plus, AlertCircle, CheckCircle2, Pencil, IndianRupee, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ExportMenu from "@/components/ExportMenu";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })
    .format(n || 0);

export default function Billing() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [branches, setBranches] = useState([]);
  const [clientId, setClientId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editRow, setEditRow] = useState(null); // full invoice edit
  const [paymentRow, setPaymentRow] = useState(null); // payment-only edit

  const canEdit = ["crm_manager", "accounts"].includes(user?.role);
  const isAdmin = user?.role === "crm_manager";

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        client_id: clientId || undefined,
        branch_id: branchId || undefined,
        month: month || undefined,
      };
      const [b, s, c, br] = await Promise.all([
        api.get("/billing", { params }),
        api.get("/billing/ar-summary", {
          params: {
            client_id: clientId || undefined,
            branch_id: branchId || undefined,
          },
        }),
        api.get("/clients", { params: { page_size: 200 } }),
        api.get("/branches"),
      ]);
      setRows(b.data.items); setSummary(s.data); setClients(c.data.items); setBranches(br.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, branchId, month]);

  const downloadTemplate = async () => {
    const t = localStorage.getItem("mptg_token");
    const res = await fetch(`${API}/billing/template`, { credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {} });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "billing_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-8 space-y-4" data-testid="billing-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mptg-overline">Finance</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Billing & Collections</h1>
          <p className="text-slate-500 mt-1">Monthly invoicing, collections, profitability</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <ExportMenu
              endpoint="/billing/export"
              params={{ client_id: clientId, branch_id: branchId, month, mode: "invoices" }}
              filename="billing_invoices"
              label="Invoices"
              testid="billing-export"
            />
            <ExportMenu
              endpoint="/billing/export"
              params={{ client_id: clientId, branch_id: branchId, month, mode: "payments" }}
              filename="billing_payments"
              label="Payments"
              testid="billing-payments-export"
            />
            <Button variant="outline" onClick={downloadTemplate} className="border-slate-200" data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-1" /> Template
            </Button>
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-slate-200" data-testid="bulk-upload-btn">
                  <Upload className="w-4 h-4 mr-1" /> Bulk upload
                </Button>
              </DialogTrigger>
              <BulkUploadDialog onDone={() => { setBulkOpen(false); load(); }} />
            </Dialog>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="new-billing-btn">
                  <Plus className="w-4 h-4 mr-1" /> New invoice
                </Button>
              </DialogTrigger>
              <NewBillingDialog clients={clients} onDone={() => { setCreateOpen(false); load(); }} />
            </Dialog>
          </div>
        )}
      </div>

      {/* AR summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card label="Total invoiced" value={inr(summary.total_invoiced)} />
          <Card label="Total received" value={inr(summary.total_received)} tone="success" />
          <Card label="Total outstanding" value={inr(summary.total_outstanding)} tone={summary.total_outstanding > 0 ? "warning" : "success"} />
          <Card label="Records" value={summary.record_count} />
          <Card label="Current" value={inr(summary.aging_breakdown.current)} />
          <Card label="30 days" value={inr(summary.aging_breakdown["30"])} tone="warning" />
          <Card label="60 days" value={inr(summary.aging_breakdown["60"])} tone="warning" />
          <Card label="90+ days" value={inr(summary.aging_breakdown["90plus"])} tone="danger" />
        </div>
      )}

      <Tabs defaultValue="invoices" className="space-y-4" data-testid="billing-tabs">
        <TabsList>
          <TabsTrigger value="invoices" data-testid="billing-tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="aging" data-testid="billing-tab-aging">Aging Dashboard</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="space-y-4">
          {/* Filters */}
          <div className="mptg-card flex flex-wrap items-center gap-3">
            <Select value={clientId || "all"} onValueChange={(v) => setClientId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-64" data-testid="billing-client-filter"><SelectValue placeholder="All clients" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clients</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={branchId || "all"} onValueChange={(v) => setBranchId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-64" data-testid="billing-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All branches</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="billing-month-filter" />
            {(clientId || branchId || month) && (
              <button
                onClick={() => { setClientId(""); setBranchId(""); setMonth(""); }}
                className="text-xs text-[#2E86C1] hover:underline"
                data-testid="billing-clear-filters"
              >
                Clear
              </button>
            )}
          </div>

          <div className="mptg-card p-0 overflow-hidden">
            <table className="w-full mptg-table">
              <thead><tr><th>Month</th><th>Client</th><th>Invoice</th><th className="text-right">Invoiced</th><th className="text-right">Received</th><th className="text-right">Outstanding</th><th>Aging</th><th>Grade</th>{canEdit && <th className="text-right">Actions</th>}</tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={canEdit ? 9 : 8} className="py-8 text-center text-slate-400">Loading…</td></tr> :
                 rows.length === 0 ? <tr><td colSpan={canEdit ? 9 : 8} className="py-8 text-center text-slate-400">No records.</td></tr> :
                  rows.map((b) => (
                    <tr key={b.id} data-testid={`billing-row-${b.id}`}>
                      <td>{b.month}</td>
                      <td>{b.client_name}</td>
                      <td className="font-mono text-xs">{b.invoice_number}</td>
                      <td className="text-right tabular-nums">{inr(b.invoice_amount)}</td>
                      <td className="text-right tabular-nums">{inr(b.amount_received)}</td>
                      <td className="text-right tabular-nums">{inr(b.outstanding_amount)}</td>
                      <td className="uppercase text-xs">{b.aging_bucket}</td>
                      <td><GradeBadge grade={b.profitability_grade} /></td>
                      {canEdit && (
                        <td className="text-right whitespace-nowrap">
                          <button
                            onClick={() => setPaymentRow(b)}
                            className="text-[#1E8449] hover:underline text-xs inline-flex items-center gap-1 mr-3"
                            data-testid={`update-payment-${b.id}`}
                          >
                            <IndianRupee className="w-3.5 h-3.5" /> Payment
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => setEditRow(b)}
                              className="text-[#2E86C1] hover:underline text-xs inline-flex items-center gap-1"
                              data-testid={`edit-invoice-${b.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" /> Edit
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="aging">
          <AgingDashboard clients={clients} branches={branches} />
        </TabsContent>
      </Tabs>

      {/* Edit invoice (admin) */}
      {editRow && (
        <Dialog open={true} onOpenChange={(v) => !v && setEditRow(null)}>
          <EditBillingDialog row={editRow} clients={clients} onDone={() => { setEditRow(null); load(); }} />
        </Dialog>
      )}

      {/* Update payment (admin + accounts) */}
      {paymentRow && (
        <Dialog open={true} onOpenChange={(v) => !v && setPaymentRow(null)}>
          <UpdatePaymentDialog row={paymentRow} isAdmin={isAdmin} onDone={() => { setPaymentRow(null); load(); }} />
        </Dialog>
      )}
    </div>
  );
}

function Card({ label, value, tone }) {
  const tones = { success: "text-[#1E8449]", warning: "text-[#F39C12]", danger: "text-[#C0392B]" };
  return (
    <div className="mptg-card">
      <div className="mptg-overline">{label}</div>
      <div className={`font-heading text-xl font-bold mt-1 ${tones[tone] || "text-slate-800"}`}>{value}</div>
    </div>
  );
}

function NewBillingDialog({ clients, onDone }) {
  const [f, setF] = useState({
    client_id: "", month: new Date().toISOString().slice(0, 7),
    invoice_number: "", invoice_amount: 0,
    invoice_date: new Date().toISOString().slice(0, 10),
    amount_received: 0, payment_date: "", payment_mode: "NEFT",
    profitability_grade: "Beta",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.client_id || !f.invoice_number) { toast.error("Client & invoice number required"); return; }
    setSaving(true);
    try {
      const payload = { ...f, payment_date: f.payment_date || null };
      await api.post("/billing", payload);
      toast.success("Invoice recorded"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader><DialogTitle>New invoice</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3 py-2">
        <div className="col-span-2"><Label>Client</Label>
          <Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v })}>
            <SelectTrigger data-testid="bill-client-select"><SelectValue placeholder="Pick client" /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Month</Label><Input type="month" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} /></div>
        <div><Label>Invoice number</Label><Input value={f.invoice_number} onChange={(e) => setF({ ...f, invoice_number: e.target.value })} data-testid="bill-invoice-num" /></div>
        <div><Label>Invoice amount</Label><Input type="number" value={f.invoice_amount} onChange={(e) => setF({ ...f, invoice_amount: +e.target.value })} /></div>
        <div><Label>Invoice date</Label><Input type="date" value={f.invoice_date} onChange={(e) => setF({ ...f, invoice_date: e.target.value })} /></div>
        <div><Label>Amount received</Label><Input type="number" value={f.amount_received} onChange={(e) => setF({ ...f, amount_received: +e.target.value })} /></div>
        <div><Label>Payment date</Label><Input type="date" value={f.payment_date} onChange={(e) => setF({ ...f, payment_date: e.target.value })} /></div>
        <div><Label>Payment mode</Label><Input value={f.payment_mode} onChange={(e) => setF({ ...f, payment_mode: e.target.value })} /></div>
        <div><Label>Profitability grade</Label>
          <Select value={f.profitability_grade} onValueChange={(v) => setF({ ...f, profitability_grade: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["Alpha", "Beta", "Gama"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-billing-btn">Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function BulkUploadDialog({ onDone }) {
  const fileRef = useRef(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const onUpload = async () => {
    const file = fileRef.current.files[0];
    if (!file) { toast.error("Pick a file"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/billing/bulk-upload/validate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!result) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/bulk-upload/commit", { rows: result.rows });
      toast.success(`Imported ${data.inserted} rows`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-3xl">
      <DialogHeader><DialogTitle>Bulk upload billing</DialogTitle></DialogHeader>
      <div className="space-y-3 py-2">
        <div className="flex items-center gap-2">
          <Input type="file" ref={fileRef} accept=".csv,.xlsx,.xls" data-testid="bulk-file-input" />
          <Button onClick={onUpload} disabled={busy} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="validate-bulk-btn">
            {busy ? "Validating…" : "Validate"}
          </Button>
        </div>
        {result && (
          <div className="space-y-2">
            <div className="flex items-center gap-4 text-sm">
              <span className="badge-green rounded px-2 py-0.5 font-semibold inline-flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> {result.valid_count} valid
              </span>
              {result.errors.length > 0 && (
                <span className="badge-red rounded px-2 py-0.5 font-semibold inline-flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> {result.errors.length} errors
                </span>
              )}
              <span className="text-slate-500 text-xs">Total rows: {result.total_count}</span>
            </div>
            <div className="border border-slate-200 rounded max-h-80 overflow-auto">
              <table className="w-full mptg-table">
                <thead><tr><th>Row</th><th>Client</th><th>Month</th><th>Invoice</th><th>Amount</th><th>Errors</th></tr></thead>
                <tbody>
                  {result.rows.map((r, i) => (
                    <tr key={i} className={r._errors.length ? "bg-red-50" : ""}>
                      <td className="tabular-nums">{r._row}</td>
                      <td>{r.client_name}</td>
                      <td>{r.month}</td>
                      <td className="font-mono text-xs">{r.invoice_number}</td>
                      <td className="tabular-nums">{inr(r.invoice_amount)}</td>
                      <td className="text-xs text-[#C0392B]">{r._errors.join("; ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <DialogFooter>
        {result && result.valid_count > 0 && (
          <Button onClick={commit} disabled={busy} className="bg-[#1E8449] hover:bg-[#1a6e3e] text-white" data-testid="commit-bulk-btn">
            Commit {result.valid_count} rows
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  );
}

function EditBillingDialog({ row, clients, onDone }) {
  const [f, setF] = useState({
    client_id: row.client_id,
    month: row.month,
    invoice_number: row.invoice_number,
    invoice_amount: row.invoice_amount || 0,
    invoice_date: (row.invoice_date || "").slice(0, 10),
    amount_received: row.amount_received || 0,
    payment_date: (row.payment_date || "").slice(0, 10),
    payment_mode: row.payment_mode || "",
    profitability_grade: row.profitability_grade || "Beta",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, payment_date: f.payment_date || null };
      await api.patch(`/billing/${row.id}`, payload);
      toast.success("Invoice updated");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <DialogContent className="max-w-2xl" data-testid="edit-billing-dialog">
      <DialogHeader><DialogTitle>Edit invoice — {row.invoice_number}</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3 py-2">
        <div className="col-span-2"><Label>Client</Label>
          <Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Month</Label><Input type="month" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} /></div>
        <div><Label>Invoice number</Label><Input value={f.invoice_number} onChange={(e) => setF({ ...f, invoice_number: e.target.value })} /></div>
        <div><Label>Invoice amount</Label><Input type="number" value={f.invoice_amount} onChange={(e) => setF({ ...f, invoice_amount: +e.target.value })} /></div>
        <div><Label>Invoice date</Label><Input type="date" value={f.invoice_date} onChange={(e) => setF({ ...f, invoice_date: e.target.value })} /></div>
        <div><Label>Amount received</Label><Input type="number" value={f.amount_received} onChange={(e) => setF({ ...f, amount_received: +e.target.value })} data-testid="edit-received" /></div>
        <div><Label>Payment date</Label><Input type="date" value={f.payment_date} onChange={(e) => setF({ ...f, payment_date: e.target.value })} /></div>
        <div><Label>Payment mode</Label><Input value={f.payment_mode} onChange={(e) => setF({ ...f, payment_mode: e.target.value })} placeholder="NEFT / RTGS / Cheque" /></div>
        <div><Label>Profitability grade</Label>
          <Select value={f.profitability_grade} onValueChange={(v) => setF({ ...f, profitability_grade: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["Alpha", "Beta", "Gama"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="save-edit-billing">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function UpdatePaymentDialog({ row, onDone, isAdmin }) {
  const [rec, setRec] = useState(row);
  const [form, setForm] = useState({
    amount: "",
    tds: "",
    other_deductions: "",
    date: new Date().toISOString().slice(0, 10),
    mode: "NEFT",
    reference: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Always pull the authoritative record (with payments[] list)
    api.get(`/billing/record/${row.id}`).then(({ data }) => setRec(data));
  }, [row.id]);

  const payments = rec.payments || [];
  const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const totalTds = payments.reduce((s, p) => s + (p.tds || 0), 0);
  const totalDed = payments.reduce((s, p) => s + (p.other_deductions || 0), 0);
  const totalSettled = totalPaid + totalTds + totalDed;
  const outstanding = Math.max(0, (rec.invoice_amount || 0) - totalSettled);

  const addPayment = async () => {
    const amt = +form.amount || 0;
    const tds = +form.tds || 0;
    const oth = +form.other_deductions || 0;
    if ((amt + tds + oth) <= 0) { toast.error("Enter at least one of Amount / TDS / Other deductions"); return; }
    if (!form.date) { toast.error("Payment date is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/billing/${rec.id}/payments`, {
        amount: amt, tds, other_deductions: oth,
        date: form.date, mode: form.mode, reference: form.reference,
      });
      setRec(data);
      setForm({ amount: "", tds: "", other_deductions: "", date: new Date().toISOString().slice(0, 10), mode: "NEFT", reference: "" });
      toast.success(data.status === "paid" ? "Payment recorded — invoice fully paid" : "Payment recorded");
      onDone(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removePayment = async (pid) => {
    if (!window.confirm("Delete this payment? The invoice will reopen.")) return;
    try {
      const { data } = await api.delete(`/billing/${rec.id}/payments/${pid}`);
      setRec(data);
      toast.success("Payment removed");
      onDone(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <DialogContent className="max-w-2xl" data-testid="update-payment-dialog">
      <DialogHeader>
        <DialogTitle>Payments — {rec.invoice_number}</DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-5 gap-3 text-sm pb-2 border-b border-slate-100">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Invoice</div>
          <div className="font-semibold tabular-nums">{inr(rec.invoice_amount)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Received</div>
          <div className="font-semibold tabular-nums text-[#1E8449]">{inr(totalPaid)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">TDS</div>
          <div className="font-semibold tabular-nums text-slate-700">{inr(totalTds)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Other ded.</div>
          <div className="font-semibold tabular-nums text-slate-700">{inr(totalDed)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Outstanding</div>
          <div className={`font-semibold tabular-nums ${outstanding > 0 ? "text-[#C0392B]" : "text-[#1E8449]"}`}>
            {inr(outstanding)}
          </div>
        </div>
      </div>

      {/* History */}
      <div className="mt-2 max-h-[220px] overflow-auto">
        <table className="w-full mptg-table text-xs" data-testid="payments-history">
          <thead>
            <tr>
              <th>Date</th><th>Mode</th><th>Reference</th>
              <th className="text-right">Amount</th>
              <th className="text-right">TDS</th>
              <th className="text-right">Other ded.</th>
              {isAdmin && <th></th>}
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 ? (
              <tr><td colSpan={isAdmin ? 7 : 6} className="py-4 text-center text-slate-400">No payments yet.</td></tr>
            ) : payments.map((p) => (
              <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                <td>{p.date}</td>
                <td>{p.mode || "-"}</td>
                <td className="font-mono">{p.reference || "-"}</td>
                <td className="text-right tabular-nums text-[#1E8449] font-semibold">{inr(p.amount)}</td>
                <td className="text-right tabular-nums text-slate-600">{p.tds ? inr(p.tds) : "-"}</td>
                <td className="text-right tabular-nums text-slate-600">{p.other_deductions ? inr(p.other_deductions) : "-"}</td>
                {isAdmin && (
                  <td className="text-right">
                    <button onClick={() => removePayment(p.id)} className="text-[#C0392B] hover:underline" data-testid={`delete-payment-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add new payment */}
      {outstanding > 0 ? (
        <div className="mt-2 p-3 rounded border border-slate-200 bg-slate-50">
          <div className="text-xs font-semibold text-slate-600 mb-2">Record new payment</div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Amount received</Label>
              <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" data-testid="new-payment-amount" />
            </div>
            <div>
              <Label className="text-xs">TDS deducted</Label>
              <Input type="number" value={form.tds} onChange={(e) => setForm({ ...form, tds: e.target.value })} placeholder="0" data-testid="new-payment-tds" />
            </div>
            <div>
              <Label className="text-xs">Other deductions</Label>
              <Input type="number" value={form.other_deductions} onChange={(e) => setForm({ ...form, other_deductions: e.target.value })} placeholder="0" data-testid="new-payment-other" />
            </div>
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="new-payment-date" />
            </div>
            <div>
              <Label className="text-xs">Mode</Label>
              <Input value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} placeholder="NEFT / UPI" />
            </div>
            <div>
              <Label className="text-xs">Reference</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="UTR / Txn ID" />
            </div>
          </div>
          <div className="text-xs text-slate-500 mt-2">
            Quick fill:
            <button className="ml-2 text-[#2E86C1] hover:underline" onClick={() => setForm({ ...form, amount: outstanding })} data-testid="fill-outstanding">
              Outstanding ({inr(outstanding)})
            </button>
            <span className="text-slate-400"> · TDS & Other deductions count towards invoice clearance.</span>
          </div>
          <div className="flex justify-end mt-3">
            <Button onClick={addPayment} disabled={saving} className="bg-[#1E8449] hover:bg-[#1a6e3e] text-white" data-testid="save-payment-btn">
              {saving ? "Saving…" : "Record payment"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-2 p-3 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm flex items-center gap-2" data-testid="invoice-fully-paid">
          <CheckCircle2 className="w-4 h-4" /> Invoice fully paid.
        </div>
      )}

      <DialogFooter className="pt-2">
        <Button variant="outline" onClick={() => onDone(rec)} data-testid="close-payment-btn">Close</Button>
      </DialogFooter>
    </DialogContent>
  );
}


function AgingDashboard({ clients, branches }) {
  const [groupBy, setGroupBy] = useState("client");
  const [clientId, setClientId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState({ rows: [], totals: null });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/billing/aging-dashboard", {
        params: {
          group_by: groupBy,
          client_id: clientId || undefined,
          branch_id: branchId || undefined,
        },
      });
      setData(d);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [groupBy, clientId, branchId]);

  return (
    <div className="space-y-3" data-testid="aging-dashboard">
      <div className="mptg-card flex flex-wrap items-center gap-3">
        <span className="mptg-overline">Group by</span>
        <div className="inline-flex border border-slate-200 rounded overflow-hidden">
          <button
            onClick={() => setGroupBy("client")}
            className={`px-3 py-1.5 text-xs font-semibold ${groupBy === "client" ? "bg-[#2E86C1] text-white" : "text-slate-600 hover:bg-slate-50"}`}
            data-testid="aging-group-client"
          >Client</button>
          <button
            onClick={() => setGroupBy("branch")}
            className={`px-3 py-1.5 text-xs font-semibold ${groupBy === "branch" ? "bg-[#2E86C1] text-white" : "text-slate-600 hover:bg-slate-50"}`}
            data-testid="aging-group-branch"
          >Branch</button>
        </div>
        <Select value={clientId || "all"} onValueChange={(v) => setClientId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="aging-client-filter"><SelectValue placeholder="All clients" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={branchId || "all"} onValueChange={(v) => setBranchId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="aging-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All branches</SelectItem>
            {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {(clientId || branchId) && (
          <button onClick={() => { setClientId(""); setBranchId(""); }} className="text-xs text-[#2E86C1] hover:underline" data-testid="aging-clear-filters">
            Clear
          </button>
        )}
        <div className="ml-auto">
          <ExportMenu
            endpoint="/billing/aging-export"
            params={{ group_by: groupBy, client_id: clientId, branch_id: branchId }}
            filename={`aging_${groupBy}_dashboard`}
            label="Export aging"
            testid="aging-export"
          />
        </div>
      </div>

      {data.totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card label="Outstanding" value={inr(data.totals.outstanding)} tone={data.totals.outstanding > 0 ? "danger" : "success"} />
          <Card label="0-30 days" value={inr(data.totals.current)} />
          <Card label="30-60 days" value={inr(data.totals["30"])} tone="warning" />
          <Card label="60-90 days" value={inr(data.totals["60"])} tone="warning" />
          <Card label="90+ days" value={inr(data.totals["90plus"])} tone="danger" />
        </div>
      )}

      <div className="mptg-card p-0 overflow-hidden">
        <table className="w-full mptg-table">
          <thead>
            <tr>
              <th>{groupBy === "branch" ? "Branch" : "Client"}</th>
              <th className="text-right">Invoices</th>
              <th className="text-right">Invoiced</th>
              <th className="text-right">Received</th>
              <th className="text-right">Outstanding</th>
              <th className="text-right">0-30d</th>
              <th className="text-right">30-60d</th>
              <th className="text-right">60-90d</th>
              <th className="text-right">90+ days</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="py-8 text-center text-slate-400">Loading…</td></tr>
            ) : data.rows.length === 0 ? (
              <tr><td colSpan={9} className="py-8 text-center text-slate-400">No outstanding amounts. ✨</td></tr>
            ) : data.rows.map((r) => (
              <tr key={r.key} data-testid={`aging-row-${r.key}`}>
                <td className="font-semibold">{r.label}</td>
                <td className="text-right tabular-nums text-slate-500">{r.invoice_count}</td>
                <td className="text-right tabular-nums">{inr(r.invoiced)}</td>
                <td className="text-right tabular-nums text-[#1E8449]">{inr(r.received)}</td>
                <td className={`text-right tabular-nums font-semibold ${r.outstanding > 0 ? "text-[#C0392B]" : "text-slate-400"}`}>{inr(r.outstanding)}</td>
                <td className="text-right tabular-nums">{inr(r.current)}</td>
                <td className="text-right tabular-nums text-[#F39C12]">{inr(r["30"])}</td>
                <td className="text-right tabular-nums text-[#F39C12]">{inr(r["60"])}</td>
                <td className="text-right tabular-nums text-[#C0392B]">{inr(r["90plus"])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
